import { useState, useEffect } from "react";
import { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Button } from "components/Button";
import { Loader } from "components/Loader";
import {
  getAvailableBrokers,
  getBrokerConnectionLink,
} from "features/Dashboard/service";
import { getBrokerIcon, parseErrorMessage, openNewTab } from "utils/index";
import { toast } from "Layout/slice";
import { trackEvent } from "utils/tracking";
// import { WELCOME_CONNECTED_PATH, WELCOME_PATH } from "features/Auth/routes";
import { DASHBOARD_PATH } from "features/Dashboard/routes";
import SurmountAccountCard from "shared/SurmountAccountCard";
import PropTypes from "prop-types";
import { Connect } from "assets/icons/index";
import { isIBKR } from "utils/index";
import { BLOCKED_BROKERS } from "utils/constants";
import { useNavigation } from "utils/navigate";
import {
  BrokerSelectionContainer,
  SurmountCardWrapper,
} from "./SnaptradeModal.styles";
import { userDetailSelect } from "Layout/slice/selector";
import { useConnectedAccounts } from "features/Dashboard/hook/useConnectedAccounts";
import { CreateSurmountAccountModal } from "shared/Modal/CreateSurmountAccountModal";

const StepBrokerSelection = ({ show, onHide, onBack, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { navigateToDashboard } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);
  const [brokers, setBrokers] = useState([]);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showSurmountModal, setShowSurmountModal] = useState(false);

  const userDetails = useSelector(userDetailSelect);
  const { portfolios } = useConnectedAccounts();

  const shouldShowCreateSurmountAccount =
    userDetails?.is_surmount_internal_created === false;

  const hasPendingSurmountAccount = portfolios?.some(
    (account) =>
      account.broker === "SURMOUNT" &&
      account.authentication_status === "pending"
  );

  const shouldShowSurmountCard =
    shouldShowCreateSurmountAccount && !hasPendingSurmountAccount;

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const data = await getAvailableBrokers();
        const availableBrokers = Array.isArray(data) ? data : [];
        setBrokers(
          availableBrokers.filter(
            (broker) => !BLOCKED_BROKERS.includes(broker?.slug)
          )
        );
      } catch (error) {
        dispatch(
          toast({
            type: "error",
            body: parseErrorMessage(error),
          })
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dispatch]);

  const handleBrokerClick = (broker) => {
    setSelectedBroker(broker);
  };

  const handleSurmountClick = () => {
    setShowSurmountModal(true);
  };

  const handleSurmountModalClose = () => {
    setShowSurmountModal(false);
  };

  const handleNext = async () => {
    if (selectedBroker) {
      try {
        setIsConfirming(true);
        const redirectionUrl =
          pathname === DASHBOARD_PATH ? DASHBOARD_PATH : DASHBOARD_PATH;
        const params = {
          broker: selectedBroker.slug,
          redirect_url: redirectionUrl,
        };
        console.log(params);
        const data = await getBrokerConnectionLink(params);
        if (data.redirect_url) {
          openNewTab(data.redirect_url, "_self");
        } else if (data.success) {
          // dispatch(
          //   toast({
          //     type: "info",
          //     body: "Surmount Paper account has been successfully connected!",
          //   })
          // );
          onSuccess();
          navigateToDashboard();
        }
        trackEvent({
          name: "connect_broker",
          label: "Broker",
          broker_name: selectedBroker.display_name,
          broker_slug: selectedBroker.slug,
        });
        onHide();
      } catch (error) {
        trackEvent({
          name: "connect_broker_failed",
          label: "Broker",
          broker_name: selectedBroker?.display_name,
          error: parseErrorMessage(error),
        });
        dispatch(
          toast({
            type: "error",
            body: parseErrorMessage(error),
          })
        );
      } finally {
        setIsConfirming(false);
      }
    }
  };

  return (
    <BrokerSelectionContainer show={show} onHide={onHide}>
      <div className="modal-content">
        {/* Top Icon */}
        <div className="top-imag">
          <img src={Connect} alt="User 2" />
        </div>

        <h2 className="heading">Choose the broker you want to connect with</h2>

        {isLoading && (
          <div className="loader-wrapper">
            <Loader />
          </div>
        )}

        {!isLoading && (
          <>
            {shouldShowSurmountCard && (
              <SurmountCardWrapper>
                <SurmountAccountCard onClick={handleSurmountClick} />
              </SurmountCardWrapper>
            )}

            <div className="broker-grid">
              {brokers.map((broker, index) => (
                <div
                  key={broker.slug + index}
                  className={`broker-card ${
                    selectedBroker?.slug === broker.slug ? "selected" : ""
                  }`}
                  onClick={() => handleBrokerClick(broker)}
                >
                  <img
                    src={getBrokerIcon(broker.square_logo_url, theme.mode)}
                    alt={broker.display_name}
                  />
                  <span>{broker.display_name}</span>

                  {!broker?.can_trade && <span>Trading not yet supported</span>}

                  {isIBKR(broker) && (
                    <span>Connection takes 24-48 hours to become active.</span>
                  )}
                </div>
              ))}
            </div>

            <div className="action-buttons">
              <Button
                variant="secondary"
                size="large"
                width="49%"
                onClick={onBack}
              >
                Cancel
              </Button>
              <Button
                size="large"
                width="49%"
                onClick={handleNext}
                disabled={!selectedBroker || isConfirming}
              >
                {isConfirming ? <Loader size="sm" /> : "Next"}
              </Button>
            </div>
          </>
        )}
      </div>

      <CreateSurmountAccountModal
        show={showSurmountModal}
        onHide={handleSurmountModalClose}
      />
    </BrokerSelectionContainer>
  );
};

StepBrokerSelection.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onBack: PropTypes.func,
  onSuccess: PropTypes.func,
};

export default StepBrokerSelection;
